import { z } from 'zod';
import { QUALITY_RULE_KINDS, QUALITY_RULE_LIMITS } from '../knowledge.js';
import { WRITING_ADDRESS_FORMS, WRITING_EMOJI_USES, WRITING_HASHTAG_USES, WRITING_HUMOURS, WRITING_NARRATIVE_VOICES, WRITING_PARAGRAPH_STYLES, WRITING_SENTENCE_LENGTHS, WRITING_STYLE_LIMITS, } from '../writing-style.js';
import { FEED_STYLE_CHOICE_FIELDS, normalizeFeedText } from './feed-analyst.js';
/**
 * `style_learner` (spec 80 section 4.16, S200-5, REQ-80-032/033): reads how
 * people changed AI-written texts before approving them and proposes style
 * lessons (phrases to avoid or to prefer, quality rules, style rules), each
 * with the edits it relies on. The run has no effect: a person answers every
 * lesson and applies it through the writing style or the quality rules.
 */
export const STYLE_LEARNER_LIMITS = {
    /** Edit pairs a start on demand needs in its window (409 `not-enough-edits` below). */
    minEdits: 5,
    /** Edit pairs the monthly sweep needs in a property's window. */
    sweepMinEdits: 10,
    /** Without an earlier succeeded run the window starts this many days ago. */
    windowDays: 180,
    /** Pairs read from module 40 per run. */
    sourceLimit: 100,
    /** Edits sent to the provider, newest first. */
    maxContextEdits: 60,
    /** Word-level difference segments per side and edit. */
    maxDiffSegments: 10,
    maxSegmentLength: 80,
    /** Phrases of one to `maxPhraseWords` words removed in at least `frequentRemovalMinEdits` edits; the top `maxFrequentRemovals`. */
    maxPhraseWords: 3,
    frequentRemovalMinEdits: 2,
    maxFrequentRemovals: 20,
    /** Rejected lessons of this many days are sent as `dismissed`. */
    dismissedDays: 180,
    summaryMaxLength: 600,
    questionMaxLength: 200,
    maxProposals: 8,
    evidenceMin: 3,
    evidenceMax: 10,
    /** Before/after examples stored per lesson, each this many characters around the first difference. */
    maxExamples: 3,
    exampleRadius: 60,
    phraseMaxLength: Math.min(WRITING_STYLE_LIMITS.avoidPhraseMaxLength, WRITING_STYLE_LIMITS.favouritePhraseMaxLength),
    ruleTextMaxLength: QUALITY_RULE_LIMITS.textMaxLength,
};
/** An edit reference of the context: `e1` is the newest edit. */
export const STYLE_EDIT_REF_PATTERN = /^e[1-9][0-9]?$/;
export const STYLE_LESSON_KINDS = ['avoid_phrase', 'favourite_phrase', 'quality_rule', 'style_rule'];
/** The values each writing-style scalar may take (spec 20 section 4). */
export const STYLE_RULE_VALUES = {
    address_form: WRITING_ADDRESS_FORMS,
    emoji_use: WRITING_EMOJI_USES,
    hashtag_use: WRITING_HASHTAG_USES,
    paragraph_style: WRITING_PARAGRAPH_STYLES,
    narrative_voice: WRITING_NARRATIVE_VOICES,
    sentence_length: WRITING_SENTENCE_LENGTHS,
    humour: WRITING_HUMOURS,
};
export const styleLearnerInputSchema = z.strictObject({ property_id: z.uuid() });
const text = (max) => z.string().trim().min(1).max(max);
const phraseValue = z.object({ phrase: text(STYLE_LEARNER_LIMITS.phraseMaxLength) });
const ruleValue = z.object({ rule_kind: z.enum(QUALITY_RULE_KINDS), text: text(STYLE_LEARNER_LIMITS.ruleTextMaxLength) });
/** The scalar's value is checked against `STYLE_RULE_VALUES` after zod, so a wrong pairing gets the retry turn. */
const styleValue = z.object({ field: z.enum(FEED_STYLE_CHOICE_FIELDS), value: text(40) });
export const styleLessonValueSchema = z.union([phraseValue, ruleValue, styleValue]);
export const styleLearnerOutputSchema = z.object({
    summary: text(STYLE_LEARNER_LIMITS.summaryMaxLength),
    proposals: z
        .array(z.object({
        kind: z.enum(STYLE_LESSON_KINDS),
        question: text(STYLE_LEARNER_LIMITS.questionMaxLength),
        value: styleLessonValueSchema,
        evidence: z.array(z.string().regex(STYLE_EDIT_REF_PATTERN)).min(STYLE_LEARNER_LIMITS.evidenceMin).max(STYLE_LEARNER_LIMITS.evidenceMax),
    }))
        .max(STYLE_LEARNER_LIMITS.maxProposals),
});
/**
 * A comparable form of a lesson: kind plus the normalised value. Two lessons
 * with the same identity are the same proposal (dismissed, stored or twice).
 */
export function styleLessonIdentity(kind, value) {
    if ('phrase' in value)
        return `${kind}:${normalizeFeedText(value.phrase)}`;
    if ('rule_kind' in value)
        return `${kind}:${value.rule_kind}:${normalizeFeedText(value.text)}`;
    return `${kind}:${value.field}=${value.value}`;
}
/** The proposal keys of a stored `style_learner` output: `lesson.<index>`. */
export function styleLearnerProposalKeys(stored) {
    return stored.proposals.map((proposal) => proposal.key);
}
//# sourceMappingURL=style-learner.js.map